/* ==========================================================================
   Gear Ratio and RPM Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-grc]');
  if (!root) return;

  var INCHES_PER_MILE = 63360;

  /* Common light truck and SUV ring and pinion ratios, used to suggest the
     nearest real gear set rather than an arbitrary decimal. */
  var COMMON_RATIOS = [3.07, 3.21, 3.31, 3.42, 3.55, 3.73, 3.90, 4.10,
                       4.30, 4.56, 4.88, 5.13, 5.38];

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function nearestRatio(target) {
    var best = COMMON_RATIOS[0], bestGap = Math.abs(target - best);
    COMMON_RATIOS.forEach(function (r) {
      var gap = Math.abs(target - r);
      if (gap < bestGap) { best = r; bestGap = gap; }
    });
    return best;
  }

  function compute(input) {
    if (!isFinite(input.tire) || input.tire <= 0) {
      return { ok: false, errors: ['Enter your tire diameter in inches.'] };
    }
    if (!isFinite(input.axle) || input.axle <= 0) {
      return { ok: false, errors: ['Enter your axle ratio, for example 3.55.'] };
    }
    if (!isFinite(input.gear) || input.gear <= 0) {
      return { ok: false, errors: ['Enter the transmission gear ratio. Use 1.00 for direct drive.'] };
    }
    var speed = isFinite(input.speed) && input.speed > 0 ? input.speed : 70;

    var revsPerMile = INCHES_PER_MILE / (Math.PI * input.tire);
    var rpm = (speed * revsPerMile * input.axle * input.gear) / 60;
    var mphPer1000 = 1000 * 60 / (revsPerMile * input.axle * input.gear);

    var status;
    if (rpm < 1500) {
      status = { key: 'soon', label: num(rpm, 0) + ' RPM',
                 note: 'Low. Fine for economy on flat ground, but expect the transmission to hunt for gears on hills or towing.' };
    } else if (rpm <= 2600) {
      status = { key: 'good', label: num(rpm, 0) + ' RPM',
                 note: 'A comfortable cruise. This is where most drivetrains are happiest.' };
    } else if (rpm <= 3200) {
      status = { key: 'monitor', label: num(rpm, 0) + ' RPM',
                 note: 'On the busy side for a highway cruise. Workable, but louder and thirstier.' };
    } else {
      status = { key: 'now', label: num(rpm, 0) + ' RPM',
                 note: 'High for sustained cruising. Worth looking at a taller gear or an overdrive.' };
    }

    var rows = [
      { head: 'At ' + num(speed, 0) + ' mph' },
      { label: 'Engine speed', value: num(rpm, 0) + ' RPM' },
      { label: 'Revs per mile', value: num(revsPerMile, 0) },
      { label: 'MPH per 1000 RPM', value: num(mphPer1000, 1) },
      { head: 'RPM at other speeds' }
    ];
    [30, 45, 55, 65, 75].forEach(function (s) {
      rows.push({ label: s + ' mph',
                  value: num((s * revsPerMile * input.axle * input.gear) / 60, 0) + ' RPM' });
    });

    var figures = [
      { label: 'Engine speed at ' + num(speed, 0) + ' mph', value: num(rpm, 0) + ' RPM',
        sub: num(mphPer1000, 1) + ' mph per 1,000 RPM in this gear.' }
    ];

    var warnings = [];
    if (isFinite(input.stockTire) && input.stockTire > 0 &&
        Math.abs(input.stockTire - input.tire) > 0.05) {
      var needed = input.axle * (input.tire / input.stockTire);
      var suggest = nearestRatio(needed);
      var pct = ((input.tire - input.stockTire) / input.stockTire) * 100;
      figures.push({
        label: 'To restore stock gearing',
        value: num(suggest, 2) + ':1',
        sub: 'Tires are ' + signed(pct, 1) + '% ' + (pct > 0 ? 'taller' : 'shorter')
           + '. Exact match would be ' + num(needed, 2) + ':1.'
      });
      rows.push({ head: 'After the tire change' });
      rows.push({ label: 'Effective ratio now', value: num(input.axle * (input.stockTire / input.tire), 2) + ':1' });
      rows.push({ label: 'Ratio needed', value: num(needed, 2) + ':1' });
      rows.push({ label: 'Nearest common gear', value: num(suggest, 2) + ':1' });
      if (pct > 8) {
        warnings.push('Tires that much taller take a real bite out of low end response and towing ability. Re-gearing is usually the fix.');
      }
    }

    return { ok: true, errors: [], warnings: warnings, status: status,
             figures: figures, rows: rows };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "tire",
          "key": "d",
          "type": "number"
      },
      {
          "name": "axle",
          "key": "a",
          "type": "number"
      },
      {
          "name": "gear",
          "key": "g",
          "type": "number"
      },
      {
          "name": "speed",
          "key": "s",
          "type": "number"
      },
      {
          "name": "stockTire",
          "key": "st",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-grc__figure">'
        + '<div class="pc-grc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-grc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-grc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-grc__row pc-grc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-grc__row">'
        + '<span class="pc-grc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-grc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_GRC = { compute: compute };
})();
